// utils/periodFilter.js
// Shared "Daily / Weekly / Monthly / Custom" period-range logic for client-side
// list filtering (Leads, Properties, Deals, Site Visits). Weekly always means
// the Wed→Mon working week containing the anchor date, matching the MIS Report.

export const PERIODS = [
  { label: "Daily",   value: "daily"   },
  { label: "Weekly",  value: "weekly"  },
  { label: "Monthly", value: "monthly" },
  { label: "Custom",  value: "custom"  },
  { label: "All",     value: "all"     },
];

export const toISODate = (d) => {
  const z = new Date(d);
  z.setMinutes(z.getMinutes() - z.getTimezoneOffset());
  return z.toISOString().slice(0, 10);
};

export const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };

/** The Wed→Mon working week (6 days) that contains `anchor`. */
export function getWedMonRange(anchor) {
  const d = new Date(anchor);
  const daysSinceWed = (d.getDay() - 3 + 7) % 7; // Sun=0..Sat=6, Wed=3
  const start = new Date(d);
  start.setDate(start.getDate() - daysSinceWed);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 5); // Wed + 5 = Mon
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/**
 * Resolve the {start, end} window for a period selector.
 * `period`: "daily" | "weekly" | "monthly" | "custom" | "all"
 * `weekAnchor`: Date — which Wed–Mon week to show for "weekly"
 * `customFrom`/`customTo`: "YYYY-MM-DD" strings for "custom"
 * Returns null for "all" (no filtering).
 */
export function getPeriodRange(period, { weekAnchor, customFrom, customTo } = {}) {
  const now = new Date();
  if (period === "all") return null;
  if (period === "custom") {
    if (!customFrom || !customTo) return null;
    const start = new Date(customFrom);
    start.setHours(0, 0, 0, 0);
    const end = new Date(customTo);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  if (period === "daily") {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { start, end: now };
  }
  if (period === "weekly") {
    return getWedMonRange(weekAnchor || now);
  }
  // monthly
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return { start, end: now };
}

/** True if `dateValue` falls inside `range` ({start,end} from getPeriodRange). A null range always matches. */
export function isDateInRange(dateValue, range) {
  if (!range) return true;
  if (!dateValue) return false;
  const t = new Date(dateValue).getTime();
  if (isNaN(t)) return false;
  return t >= range.start.getTime() && t <= range.end.getTime();
}

/** True if ANY of the given dates falls inside `range` — for records with more than one meaningful date. */
export function isAnyDateInRange(dateValues, range) {
  if (!range) return true;
  return dateValues.some((d) => isDateInRange(d, range));
}
